import { createClient } from '@supabase/supabase-js';
import { getFreshAccessToken } from '@/lib/supabase-access-token';
import type { Database } from './types';

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    persistSession: true,
    autoRefreshToken: true,
  }
});

// ── Edge-function invoke wrapper ────────────────────────────────────────────
//
// Supabase rotated this project's JWT signing keys to asymmetric (ES256). The
// current Edge Functions gateway image rejects ES256 Authorization bearers
// with `UNAUTHORIZED_UNSUPPORTED_TOKEN_ALGORITHM` — even when `verify_jwt =
// false` is set on the function. See github.com/supabase/supabase/issues/42244.
//
// To unblock every authenticated edge function without rewriting 30+ call
// sites, we override the FunctionsClient.invoke method so it:
//   1. Swaps the Authorization header to the HS256 anon key (always accepted
//      by the gateway).
//   2. Forwards the user's current session token in an `x-user-token` custom
//      header for server-side validation via admin.auth.getUser(token).
//
// Edge functions read `x-user-token` via the shared authenticateRequest helper
// in supabase/functions/_shared/auth.ts.
{
  type InvokeFn = (...args: unknown[]) => Promise<unknown>;
  const functionsClient = supabase.functions as unknown as { invoke: InvokeFn };
  const originalInvoke = functionsClient.invoke.bind(supabase.functions);

  functionsClient.invoke = async (...args: unknown[]) => {
    const [functionName, options] = args as [
      string,
      { headers?: Record<string, string>; [key: string]: unknown } | undefined,
    ];
    const opts = { ...(options ?? {}) };
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };

    // Always route through the anon key so the gateway's legacy HS256
    // verifier accepts the request.
    headers.Authorization = `Bearer ${SUPABASE_ANON_KEY}`;

    // Forward the user JWT for server-side validation unless the caller
    // already supplied one (e.g. anonymous / public functions).
    if (!headers['x-user-token']) {
      try {
        const token = await getFreshAccessToken(supabase);
        if (token) headers['x-user-token'] = token;
      } catch {
        // Session lookup failures fall through — function will return 401
        // if it requires auth, which callers already handle.
      }
    }

    opts.headers = headers;
    return originalInvoke(functionName, opts);
  };
}

// ── Storage upload wrapper ──────────────────────────────────────────────────
//
// Same ES256 problem as edge functions, but for Storage: this project's
// asymmetric (ES256) user token is not validated by the Storage API, so a direct
// `supabase.storage.from(bucket).upload(...)` reaches Storage as anonymous
// (`auth.uid()` is null) and the bucket's RLS INSERT policy rejects it with
// "new row violates row-level security policy".
//
// We transparently reroute every `.upload()` through a server-minted signed
// upload URL: `/api/storage/signed-upload` authenticates the caller (GoTrue
// accepts ES256), authorizes the (bucket, path) against the same rules as the
// RLS policies, and mints a signed URL with the service role. The signed token
// then authorizes the PUT via `uploadToSignedUrl`, so RLS is satisfied without
// the user JWT. Call sites keep using `.upload(...)` unchanged.
{
  type UploadFn = (
    path: string,
    body: unknown,
    options?: ({ upsert?: boolean } & Record<string, unknown>) | undefined,
  ) => Promise<{ data: unknown; error: unknown }>;
  type BucketApi = {
    upload: UploadFn;
    uploadToSignedUrl: (path: string, token: string, body: unknown, options?: unknown) => Promise<{ data: unknown; error: unknown }>;
  };
  const storageClient = supabase.storage as unknown as { from: (bucket: string) => BucketApi };
  const originalFrom = storageClient.from.bind(storageClient);

  storageClient.from = (bucketId: string): BucketApi => {
    const api = originalFrom(bucketId);
    const originalUpload = api.upload.bind(api);
    api.upload = async (path, body, options) => {
      // Server-side / non-browser callers use their own clients; fall back so a
      // relative fetch is never attempted without an origin.
      if (typeof window === "undefined") return originalUpload(path, body, options);
      try {
        const token = await getFreshAccessToken(supabase);
        const res = await fetch("/api/storage/signed-upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ bucket: bucketId, path, upsert: !!options?.upsert }),
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => null)) as { error?: string } | null;
          return { data: null, error: new Error(detail?.error || `Upload authorization failed (${res.status}).`) };
        }
        const { token: uploadToken } = (await res.json()) as { token: string };
        return api.uploadToSignedUrl(path, uploadToken, body, options);
      } catch (error) {
        return { data: null, error: error instanceof Error ? error : new Error("Upload failed.") };
      }
    };
    return api;
  };
}
